/**
 * MdStreamingRenderer — Lightweight inline React renderer for md-* templates.
 *
 * Renders card data directly in React during streaming — no Handlebars, no iframe.
 * This enables instant re-renders as append_to_card events arrive (~50ms intervals).
 * After finalize, the parent component switches to Handlebars + iframe for full styling.
 *
 * Used by both SessionView and PlaygroundView.
 */

export const STREAMABLE_TEMPLATES = new Set(['md-list', 'md-table', 'md-code', 'md-quote']);

/**
 * Render an arbitrary value inside a table cell / list item. Accepts:
 *   - string / number / boolean → rendered as-is
 *   - null / undefined → empty
 *   - React element → rendered as-is
 *   - { title, description } → `**title** — description` shape (LLM structured output)
 *   - { title }                → title
 *   - { text } / { label } / { name } / { value } → that field
 *   - any other object/array  → JSON.stringify fallback (never throws)
 *
 * Prevents React error #31 ("Objects are not valid as a React child") when a
 * streaming md-list/md-table receives a structured item before the final
 * template normalizes it to strings.
 */
function renderItem(item) {
  if (item == null) return '';
  if (typeof item === 'string' || typeof item === 'number' || typeof item === 'boolean') {
    return String(item);
  }
  // React element (already renderable)
  if (typeof item === 'object' && item.$$typeof) {
    return item;
  }
  if (typeof item === 'object' && !Array.isArray(item)) {
    // { title, description } — the shape that caused React error #31
    if ('title' in item && 'description' in item) {
      return (
        <>
          <strong>{String(item.title ?? '')}</strong>
          {item.description ? ` — ${String(item.description)}` : ''}
        </>
      );
    }
    // Common single-field shapes
    if (typeof item.title === 'string') return item.title;
    if (typeof item.text === 'string') return item.text;
    if (typeof item.label === 'string') return item.label;
    if (typeof item.name === 'string') return item.name;
    if (typeof item.value === 'string') return item.value;
  }
  // Arrays and unknown objects — JSON fallback so we never throw
  try {
    return JSON.stringify(item);
  } catch {
    return '';
  }
}

export default function MdStreamingRenderer({ template, data, darkMode = true }) {
  const colors = darkMode
    ? { text: '#e0e0e0', title: '#fff', border: 'rgba(255,255,255,0.15)', borderDim: 'rgba(255,255,255,0.08)', codeBg: 'rgba(0,0,0,0.3)', quoteBorder: 'rgba(255,255,255,0.3)' }
    : { text: '#333', title: '#111', border: 'rgba(0,0,0,0.12)', borderDim: 'rgba(0,0,0,0.06)', codeBg: 'rgba(0,0,0,0.04)', quoteBorder: 'rgba(0,0,0,0.2)' };

  const cardStyle = { padding: '16px 20px', fontSize: '14px', lineHeight: '1.6', color: colors.text };
  const titleStyle = { fontSize: '16px', fontWeight: 600, marginBottom: '12px', color: colors.title };

  if (template === 'md-list') {
    const items = data?.items || [];
    const Tag = data?.ordered ? 'ol' : 'ul';
    return (
      <div style={cardStyle}>
        {data?.title && <div style={titleStyle}>{data.title}</div>}
        <Tag style={{ margin: 0, paddingLeft: '20px' }}>
          {items.map((item, i) => (
            <li key={i} style={{ marginBottom: '8px' }}>{renderItem(item)}</li>
          ))}
        </Tag>
        {data?.footer && <div style={{ marginTop: '12px', fontSize: '12px', opacity: 0.6 }}>{data.footer}</div>}
      </div>
    );
  }

  if (template === 'md-table') {
    const headers = data?.headers || [];
    const rows = data?.rows || [];
    return (
      <div style={cardStyle}>
        {data?.title && <div style={titleStyle}>{data.title}</div>}
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
          {headers.length > 0 && (
            <thead>
              <tr>
                {headers.map((h, i) => (
                  <th key={i} style={{ textAlign: 'left', padding: '8px', borderBottom: `1px solid ${colors.border}`, fontWeight: 600, color: colors.title }}>{renderItem(h)}</th>
                ))}
              </tr>
            </thead>
          )}
          <tbody>
            {rows.map((row, ri) => (
              <tr key={ri}>
                {(Array.isArray(row) ? row : [row]).map((cell, ci) => (
                  <td key={ci} style={{ padding: '8px', borderBottom: `1px solid ${colors.borderDim}` }}>{renderItem(cell)}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  if (template === 'md-code') {
    return (
      <div style={cardStyle}>
        {data?.title && <div style={titleStyle}>{data.title}</div>}
        <pre style={{ background: colors.codeBg, padding: '12px', borderRadius: '6px', overflow: 'auto', fontSize: '13px', margin: 0 }}>
          <code>{data?.code || ''}</code>
        </pre>
      </div>
    );
  }

  if (template === 'md-quote') {
    return (
      <div style={cardStyle}>
        <blockquote style={{ borderLeft: `3px solid ${colors.quoteBorder}`, paddingLeft: '16px', margin: 0, fontStyle: 'italic' }}>
          {data?.text || ''}
        </blockquote>
        {data?.source && <div style={{ marginTop: '8px', fontSize: '12px', opacity: 0.6 }}>— {data.source}</div>}
      </div>
    );
  }

  return null;
}
